import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { AppCard } from './AppCard';
import { spacing } from '../constants/spacing';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import type { WorkflowHistoryItem } from '../types/workflow';
import { formatAttendanceTimestamp } from '../utils/dateTime';

type Props = {
  items: WorkflowHistoryItem[];
  error?: string | null;
  onRetry?: () => void;
};

export function WorkflowHistoryTimeline({ items, error, onRetry }: Props) {
  const palette = useAppColors();

  if (error) {
    return (
      <AppCard style={styles.card}>
        <Text style={[styles.section, { color: palette.textPrimary }]}>
          Workflow history
        </Text>
        <Text
          accessibilityRole="alert"
          style={[styles.error, { color: palette.error }]}
        >
          {error}
        </Text>
        {onRetry ? (
          <Text
            accessibilityRole="button"
            onPress={onRetry}
            style={[styles.retry, { color: palette.primary }]}
          >
            Retry
          </Text>
        ) : null}
      </AppCard>
    );
  }

  if (!items.length) {
    return (
      <AppCard style={styles.card}>
        <Text style={[styles.section, { color: palette.textPrimary }]}>
          Workflow history
        </Text>
        <Text style={[styles.help, { color: palette.textSecondary }]}>
          No workflow history is available yet.
        </Text>
      </AppCard>
    );
  }

  return (
    <AppCard style={styles.card}>
      <Text style={[styles.section, { color: palette.textPrimary }]}>
        Workflow history
      </Text>
      {items.map(item => (
        <View
          key={String(item.workflowHistoryId)}
          style={[styles.row, { borderTopColor: palette.border }]}
          accessibilityLabel={`${item.actionLabel}${item.stepName ? `, ${item.stepName}` : ''}`}
        >
          <Text style={[styles.action, { color: palette.textPrimary }]}>
            {item.actionLabel}
          </Text>
          {item.stepName ? (
            <Text style={[styles.meta, { color: palette.textSecondary }]}>
              Step: {item.stepName}
            </Text>
          ) : null}
          {item.actedByName ? (
            <Text style={[styles.meta, { color: palette.textSecondary }]}>
              By: {item.actedByName}
            </Text>
          ) : null}
          <Text style={[styles.meta, { color: palette.textSecondary }]}>
            {formatAttendanceTimestamp(item.actionAt, null)}
          </Text>
          {item.comments ? (
            <Text style={[styles.comment, { color: palette.textPrimary }]}>
              {item.comments}
            </Text>
          ) : null}
        </View>
      ))}
    </AppCard>
  );
}

const styles = StyleSheet.create({
  card: { gap: spacing.sm, marginBottom: spacing.md },
  section: { ...typography.sectionTitle },
  row: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: spacing.sm,
    gap: 2,
  },
  action: { ...typography.body, fontWeight: '600' },
  meta: { ...typography.caption },
  comment: { ...typography.body, marginTop: 2 },
  help: { ...typography.body },
  error: { ...typography.body },
  retry: {
    ...typography.body,
    fontWeight: '600',
    marginTop: spacing.sm,
  },
});
